/* ==========================================================================
   Ice Vault — theme.js
   Apparitions au scroll, glint, effets liés au scroll (parallaxe, texte
   défilant, section épinglée), en-tête, tiroirs, panier, recherche prédictive.
   Aucune dépendance. Chargé en différé (defer).
   Les animations ne touchent que transform et opacity.
   ========================================================================== */
(() => {
  'use strict';

  const html = document.documentElement;
  const motionOK = html.classList.contains('motion-ok');
  const glintOK = html.classList.contains('glint-ok');
  const canHover = window.matchMedia('(hover: hover)').matches;
  const hasIO = 'IntersectionObserver' in window;
  const theme = window.theme || { routes: {}, strings: {} };

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* ------------------------------------------------------------------------
     Glint : relance l'animation de reflet sur un titre ou un visuel
     ------------------------------------------------------------------------ */
  function glint(el, delay = 0) {
    if (!glintOK || !el) return;
    window.setTimeout(() => {
      el.classList.remove('is-glinting');
      void el.offsetWidth; // force le redémarrage de l'animation CSS
      el.classList.add('is-glinting');
    }, delay);
  }

  document.addEventListener('animationend', (event) => {
    if (event.animationName !== 'glint-window' && event.animationName !== 'glint-sweep') return;
    const target = event.target.closest('.is-glinting');
    if (target) target.classList.remove('is-glinting');
  });

  if (canHover && glintOK) {
    document.addEventListener('mouseover', (event) => {
      const trigger = event.target.closest('[data-glint-trigger], [data-glint]');
      if (!trigger || trigger.contains(event.relatedTarget)) return;
      const el = trigger.matches('[data-glint]') ? trigger : $('[data-glint]', trigger);
      if (el && !el.classList.contains('is-glinting')) glint(el);
    });
  }

  /* ------------------------------------------------------------------------
     Apparitions : [data-reveal] reçoit .is-in en entrant dans l'écran
     ------------------------------------------------------------------------ */
  function reveal(el) {
    el.classList.add('is-in');
    const stagger = parseFloat(el.style.getPropertyValue('--i')) || 0;
    const own = $$('[data-glint]', el).filter((g) => g.closest('[data-reveal]') === el);
    if (el.matches('[data-glint]')) own.push(el);
    own.forEach((g) => glint(g, Number(g.dataset.glintDelay) || 650 + stagger * 120));
  }

  const revealIO =
    motionOK && hasIO
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              revealIO.unobserve(entry.target);
              reveal(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0 }
        )
      : null;

  // Glint à l'apparition pour les éléments hors [data-reveal] (fiche produit…)
  const glintIO =
    glintOK && hasIO
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              glintIO.unobserve(entry.target);
              glint(entry.target, 250);
            });
          },
          { threshold: 0.35 }
        )
      : null;

  function initReveal(root) {
    $$('[data-reveal]', root).forEach((el) => {
      if (revealIO) revealIO.observe(el);
      else el.classList.add('is-in');
    });
    if (glintIO) {
      $$('[data-glint]', root).forEach((el) => {
        if (revealIO && el.closest('[data-reveal]')) return;
        glintIO.observe(el);
      });
    }
  }

  /* ------------------------------------------------------------------------
     Moteur de scroll : une seule boucle rAF, active seulement pour les
     éléments visibles (parallaxe, texte défilant, section épinglée)
     ------------------------------------------------------------------------ */
  const scrollHandlers = new Map();
  const activeScroll = new Set();
  let viewportH = window.innerHeight;
  let ticking = false;

  const scrollIO = hasIO
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) activeScroll.add(entry.target);
            else activeScroll.delete(entry.target);
          });
          requestTick();
        },
        { rootMargin: '15% 0px' }
      )
    : null;

  function updateScroll() {
    ticking = false;
    // Lecture de toutes les positions d'abord, écriture ensuite (pas de reflow forcé)
    const reads = [];
    activeScroll.forEach((el) => reads.push([el, el.getBoundingClientRect()]));
    reads.forEach(([el, rect]) => {
      const handler = scrollHandlers.get(el);
      if (handler) handler(rect);
    });
  }

  function requestTick() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateScroll);
  }

  function addScrollEffect(el, handler) {
    if (!scrollIO) return;
    scrollHandlers.set(el, handler);
    scrollIO.observe(el);
  }

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', () => {
    viewportH = window.innerHeight;
    requestTick();
  });

  // Parallaxe léger de la bannière
  function initParallax(root) {
    if (!motionOK) return;
    $$('[data-parallax]', root).forEach((media) => {
      const hero = media.closest('.hero') || media.parentElement;
      addScrollEffect(hero, (rect) => {
        const progress = clamp(-rect.top / rect.height, 0, 1);
        media.style.transform = `translate3d(0, ${(progress * 22).toFixed(2)}%, 0)`;
      });
    });
  }

  // Grand texte qui défile horizontalement au rythme du scroll
  function initMarquee(root) {
    if (!motionOK) return;
    $$('[data-marquee]', root).forEach((section) => {
      const amplitude = (Number(section.dataset.marqueeSpeed) || 30) / 100;
      const rows = $$('[data-marquee-row]', section).map((row) => ({
        row,
        dir: Number(row.dataset.dir) || 1,
      }));
      addScrollEffect(section, (rect) => {
        const progress = clamp((viewportH - rect.top) / (viewportH + rect.height), 0, 1);
        const distance = window.innerWidth * amplitude * 4;
        rows.forEach(({ row, dir }) => {
          const x = dir > 0 ? -progress * distance : -distance + progress * distance;
          row.style.transform = `translate3d(${x.toFixed(1)}px, 0, 0)`;
        });
      });
    });
  }

  // Sur-mesure : section épinglée (position: sticky), étapes pilotées par le scroll
  function initProcess(root) {
    if (!motionOK) return;
    $$('[data-process]', root).forEach((section) => {
      const steps = $$('[data-step]', section);
      if (steps.length < 2) return;
      section.classList.add('is-pinned');

      const groups = [steps, $$('[data-step-num]', section), $$('[data-step-media]', section), $$('[data-step-dot]', section)];
      const bar = $('[data-process-bar]', section);
      const track = $('.process__track', section);
      let current = -1;

      const setStep = (index) => {
        if (index === current) return;
        current = index;
        groups.forEach((list) =>
          list.forEach((el, k) => {
            el.classList.toggle('is-active', k === index);
            el.classList.toggle('is-past', k < index);
          })
        );
      };
      setStep(0);

      addScrollEffect(track, (rect) => {
        const total = rect.height - viewportH;
        const progress = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;
        if (bar) bar.style.transform = `scaleX(${progress.toFixed(4)})`;
        setStep(Math.min(steps.length - 1, Math.floor(progress * steps.length)));
      });
    });
  }

  // Étapes « ligne de lumière » : la ligne se remplit jusqu'au repère de lecture
  // (62 % de la hauteur d'écran) et chaque étape atteinte s'allume
  function initTimeline(root) {
    if (!motionOK) return;
    $$('[data-timeline]', root).forEach((track) => {
      const fill = $('[data-timeline-fill]', track);
      const steps = $$('[data-timeline-step]', track);
      track.classList.add('is-live');
      addScrollEffect(track, (rect) => {
        const progress = clamp((viewportH * 0.62 - rect.top) / rect.height, 0, 1);
        if (fill) fill.style.transform = `scaleY(${progress.toFixed(4)})`;
        const reached = progress * rect.height;
        steps.forEach((step) => {
          const lit = step.offsetTop + 24 <= reached;
          if (lit === step.classList.contains('is-lit')) return;
          step.classList.toggle('is-lit', lit);
          if (lit) glint($('.timeline__num-fill', step), 60);
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Tiroirs : <dialog> natif (piège du focus, Échap, arrière-plan inerte)
     ------------------------------------------------------------------------ */
  let openDialogs = 0;

  function openDrawer(dialog, opener) {
    if (!dialog || dialog.open) return;
    dialog.returnFocusTo = opener || document.activeElement;
    dialog.showModal();
    openDialogs += 1;
    // Double rAF : l'état fermé est peint avant la transition d'ouverture
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => dialog.classList.add('is-open')));
    const searchInput = $('input[type="search"]', dialog);
    if (searchInput) window.setTimeout(() => searchInput.focus(), 60);
  }

  function closeDrawer(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    dialog.classList.add('is-closing');
    dialog.classList.remove('is-open');
    const panel = $('.drawer__panel', dialog) || dialog;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      panel.removeEventListener('transitionend', onEnd);
      dialog.classList.remove('is-closing');
      dialog.close();
      openDialogs = Math.max(0, openDialogs - 1);
      const back = dialog.returnFocusTo;
      if (back && typeof back.focus === 'function') back.focus({ preventScroll: true });
    };
    const onEnd = (event) => {
      if (event.target === panel) finish();
    };
    panel.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, 650);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.drawerOpen);
      if (dialog) {
        event.preventDefault();
        openDrawer(dialog, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      closeDrawer(closer.closest('dialog'));
      return;
    }
    const cartLink = event.target.closest('[data-cart-open]');
    if (cartLink && document.getElementById('CartDrawer')) {
      event.preventDefault();
      openDrawer(document.getElementById('CartDrawer'), cartLink);
    }
  });

  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target.matches('dialog.drawer')) {
        event.preventDefault();
        closeDrawer(event.target);
      }
    },
    true
  );

  /* ------------------------------------------------------------------------
     En-tête : opaque au scroll, masqué en descendant (option), menus déroulants
     ------------------------------------------------------------------------ */
  let headerReady = null;

  function initHeader() {
    const header = $('[data-header]');
    if (!header || headerReady === header) return;
    headerReady = header;
    const wrapper = header.closest('.shopify-section') || header;
    const smart = header.dataset.sticky === 'smart';
    let lastY = window.scrollY;
    let queued = false;

    const onScroll = () => {
      queued = false;
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      if (smart) {
        const goingDown = y > lastY + 4;
        const goingUp = y < lastY - 4;
        if (goingDown && y > 320 && openDialogs === 0 && !wrapper.contains(document.activeElement)) {
          wrapper.classList.add('is-hidden');
        } else if (goingUp || y < 320) {
          wrapper.classList.remove('is-hidden');
        }
      }
      lastY = y;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(onScroll);
      },
      { passive: true }
    );
    onScroll();

    wrapper.addEventListener('focusin', () => wrapper.classList.remove('is-hidden'));

    const dropdowns = $$('[data-nav-dropdown]', header);
    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener('toggle', () => {
        if (dropdown.open) dropdowns.forEach((other) => other !== dropdown && (other.open = false));
      });

      // Ordinateur : ouverture au survol ; un clic souris ne referme pas un panneau ouvert
      // (le clavier garde le fonctionnement natif : Entrée ouvre et ferme)
      if (canHover) {
        const item = dropdown.closest('.nav__item') || dropdown;
        let closeTimer;
        item.addEventListener('mouseenter', () => {
          window.clearTimeout(closeTimer);
          dropdown.open = true;
        });
        item.addEventListener('mouseleave', () => {
          closeTimer = window.setTimeout(() => {
            dropdown.open = false;
          }, 180);
        });
        $('summary', dropdown).addEventListener('click', (event) => {
          if (event.detail > 0 && dropdown.open) event.preventDefault();
        });
      }

      // Survol d'un lien : l'image correspondante s'éclaire, les autres s'effacent
      const mega = $('.mega', dropdown);
      if (!mega) return;
      const tiles = $$('[data-mega-tile]', mega);
      mega.addEventListener('mouseover', (event) => {
        const link = event.target.closest('[data-mega-link]');
        const index = link ? link.dataset.megaLink : null;
        const match = tiles.find((tile) => tile.dataset.megaTile === index);
        mega.classList.toggle('is-focusing', Boolean(match));
        tiles.forEach((tile) => {
          const active = tile === match;
          if (active && !tile.classList.contains('is-active')) glint($('[data-glint]', tile));
          tile.classList.toggle('is-active', active);
        });
      });
      mega.addEventListener('mouseleave', () => {
        mega.classList.remove('is-focusing');
        tiles.forEach((tile) => tile.classList.remove('is-active'));
      });
    });
  }

  document.addEventListener('click', (event) => {
    $$('[data-nav-dropdown][open]').forEach((dropdown) => {
      if (!dropdown.contains(event.target)) dropdown.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    $$('[data-nav-dropdown][open]').forEach((dropdown) => {
      dropdown.open = false;
      $('summary', dropdown).focus();
    });
  });

  /* ------------------------------------------------------------------------
     Barre d'annonce : bouton pause
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-announce-toggle]');
    if (!toggle) return;
    const bar = toggle.closest('[data-announce]');
    const paused = bar.classList.toggle('is-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? theme.strings.play : theme.strings.pause);
  });

  /* ------------------------------------------------------------------------
     Panier en tiroir (API AJAX + Section Rendering)
     ------------------------------------------------------------------------ */
  const cartDrawer = () => document.getElementById('CartDrawer');

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function renderCart(sections) {
    const drawer = cartDrawer();
    const markup = sections && sections['cart-drawer'];
    if (!drawer || !markup) return;
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const fresh = $('[data-cart-inner]', doc);
    const current = $('[data-cart-inner]', drawer);
    if (fresh && current) {
      current.replaceWith(fresh);
      updateCartCount(Number(fresh.dataset.cartCountValue) || 0);
    }
  }

  function showCartError(message) {
    const body = $('[data-cart-inner] .drawer__body', cartDrawer());
    if (!body) return;
    let box = $('[data-cart-error]', body);
    if (!box) {
      box = document.createElement('p');
      box.className = 'form-msg form-msg--err';
      box.setAttribute('role', 'alert');
      box.dataset.cartError = '';
      body.prepend(box);
    }
    box.textContent = message;
  }

  async function postJSON(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
    return data;
  }

  let adding = false;

  async function addToCart(form) {
    if (adding) return;
    adding = true;
    const buttons = $$('[data-atc]', form).concat(form.id ? $$(`[form="${form.id}"][data-atc]`) : []);
    const errorBox = $('[data-form-error]', form);
    buttons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));
    if (errorBox) errorBox.hidden = true;

    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      renderCart(data.sections);
      openDrawer(cartDrawer(), document.activeElement);
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      }
    } finally {
      adding = false;
      buttons.forEach((btn) => btn.removeAttribute('aria-busy'));
    }
  }

  async function changeLine(line, quantity) {
    const inner = $('[data-cart-inner]', cartDrawer());
    if (inner) inner.classList.add('is-loading');
    try {
      const data = await postJSON(`${theme.routes.cartChange}.js`, {
        line,
        quantity,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname,
      });
      renderCart(data.sections);
      const drawer = cartDrawer();
      const focusTarget = $(`[data-line="${line}"][data-qty]`, drawer) || $('[data-drawer-close].icon-btn', drawer);
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    } catch (error) {
      const stale = $('[data-cart-inner]', cartDrawer());
      if (stale) stale.classList.remove('is-loading');
      showCartError(error.message);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-inner] [data-line][data-qty]');
    if (!button) return;
    event.preventDefault();
    changeLine(Number(button.dataset.line), Math.max(0, Number(button.dataset.qty)));
  });

  let noteTimer;
  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-cart-note]')) return;
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(() => {
      postJSON(`${theme.routes.cartUpdate}.js`, { note: event.target.value }).catch(() => {});
    }, 500);
  });

  /* ------------------------------------------------------------------------
     Formulaires : ajout au panier, filtres, confirmations
     ------------------------------------------------------------------------ */
  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (form.matches('[data-confirm]') && !window.confirm(form.dataset.confirm)) {
      event.preventDefault();
      return;
    }

    if (form.id === 'FacetsForm') {
      // Retire les champs vides (prix) pour garder des URL de filtres propres
      event.preventDefault();
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      window.location.href = `${form.action.split('?')[0]}?${params.toString()}`;
      return;
    }

    if (form.matches('[data-type="add-to-cart-form"]') && cartDrawer()) {
      event.preventDefault();
      addToCart(form);
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-auto-submit]')) return;
    const form = event.target.form;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  });

  /* ------------------------------------------------------------------------
     Recherche prédictive (Section Rendering, section predictive-search)
     ------------------------------------------------------------------------ */
  function initPredictiveSearch() {
    const input = $('[data-predictive-input]');
    const results = $('[data-predictive-results]');
    if (!input || !results || !theme.routes.predictiveSearch) return;
    let timer;
    let controller;

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = window.setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product,collection,page',
          'resources[limit]': '6',
          section_id: 'predictive-search',
        });
        try {
          const response = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`, { signal: controller.signal });
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = doc.querySelector('#shopify-section-predictive-search');
          results.innerHTML = section ? section.innerHTML : '';
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '';
        }
      }, 250);
    });
  }

  /* ------------------------------------------------------------------------
     Compte à rebours (page d'attente) vers une date de lancement réelle
     ------------------------------------------------------------------------ */
  function initCountdown(root) {
    $$('[data-countdown]', root).forEach((el) => {
      const target = Number(el.dataset.countdown) * 1000;
      if (!target || el.dataset.countdownReady) return;
      el.dataset.countdownReady = 'true';
      const unit = (name) => $(`[data-unit="${name}"]`, el);
      const parts = { days: unit('days'), hours: unit('hours'), minutes: unit('minutes'), seconds: unit('seconds') };
      const pad = (n) => String(n).padStart(2, '0');
      let timer;
      const tick = () => {
        const left = Math.max(0, Math.floor((target - Date.now()) / 1000));
        parts.days.textContent = pad(Math.floor(left / 86400));
        parts.hours.textContent = pad(Math.floor((left % 86400) / 3600));
        parts.minutes.textContent = pad(Math.floor((left % 3600) / 60));
        parts.seconds.textContent = pad(left % 60);
        if (left === 0) {
          window.clearInterval(timer);
          if (el.dataset.doneText) {
            const done = document.createElement('p');
            done.className = 'soon__done';
            done.textContent = el.dataset.doneText;
            el.replaceChildren(done);
            el.removeAttribute('aria-hidden');
          }
        }
      };
      tick();
      timer = window.setInterval(tick, 1000);
    });
  }

  /* ------------------------------------------------------------------------
     Produits recommandés : chargés à l'approche de la section
     ------------------------------------------------------------------------ */
  function initRecommendations(root) {
    $$('[data-recommendations]', root).forEach((el) => {
      if (el.dataset.loaded || !el.dataset.url || !hasIO) return;
      const load = async () => {
        el.dataset.loaded = 'true';
        try {
          const response = await fetch(el.dataset.url);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = $('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            initReveal(el);
          }
        } catch (error) {
          /* section laissée vide */
        }
      };
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Initialisation (et ré-initialisation dans l'éditeur de thème)
     ------------------------------------------------------------------------ */
  function init(root = document) {
    initReveal(root);
    initParallax(root);
    initMarquee(root);
    initProcess(root);
    initTimeline(root);
    initRecommendations(root);
    initCountdown(root);
    $$('dialog[data-open-on-load]', root).forEach((dialog) => openDrawer(dialog));
    requestTick();
  }

  initHeader();
  initPredictiveSearch();
  init();

  document.addEventListener('shopify:section:load', (event) => {
    initHeader();
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    scrollHandlers.forEach((_, el) => {
      if (!event.target.contains(el)) return;
      scrollHandlers.delete(el);
      activeScroll.delete(el);
      if (scrollIO) scrollIO.unobserve(el);
    });
  });

  window.IceVault = { glint, openDrawer, closeDrawer, initReveal };
})();
